import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import {
  DispatchRiderSnapshot,
  RiderReassignMode,
} from '@/services/api/dispatches';

interface ReassignBatchesModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Source rider whose batches move. Null = modal closed / not opened. */
  fromRider: DispatchRiderSnapshot | null;
  /** All riders on the dispatch; the source is filtered out of the picker. */
  riders: DispatchRiderSnapshot[];
  onSubmit: (
    fromRiderId: number,
    toRiderId: number,
    mode: RiderReassignMode
  ) => Promise<void>;
}

const MODES: Array<{ key: RiderReassignMode; label: string; hint: string }> = [
  {
    key: 'TRANSFER',
    label: 'Transfer',
    hint: "Move all of this rider's batches to the selected rider. The source rider keeps none.",
  },
  {
    key: 'SWAP',
    label: 'Swap',
    hint: "Exchange the two riders' batches — each keeps its stops and items, only the owner changes.",
  },
];

const ReassignBatchesModal: React.FC<ReassignBatchesModalProps> = ({
  isOpen,
  onClose,
  fromRider,
  riders,
  onSubmit,
}) => {
  const [mode, setMode] = useState<RiderReassignMode>('TRANSFER');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMode('TRANSFER');
      setSelectedId(null);
    }
  }, [isOpen]);

  const targets = riders.filter((r) => r.id !== fromRider?.id);
  const isSwap = mode === 'SWAP';

  const handleSubmit = async () => {
    if (!fromRider || selectedId == null) return;
    setSubmitting(true);
    try {
      await onSubmit(fromRider.id, selectedId, mode);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && fromRider != null && selectedId != null;

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-arrow-left-right-line" />}
      iconTone="primary"
      title={isSwap ? 'Swap batches' : 'Reassign all batches'}
      subtitle={fromRider ? `From ${fromRider.name} · ${fromRider.code}` : undefined}
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting
              ? isSwap
                ? 'Swapping…'
                : 'Reassigning…'
              : isSwap
              ? 'Swap batches'
              : 'Reassign'}
          </Button>
        </div>
      }
    >
      {/* Mode toggle — Transfer (one-way) vs Swap (two-way). */}
      <div className="mb-4">
        <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
          Mode
        </p>
        <SegmentedControl
          value={mode}
          onValueChange={(v: string) => {
            if (v) setMode(v as RiderReassignMode);
          }}
        >
          {MODES.map((m) => (
            <SegmentedItem key={m.key} value={m.key} disabled={submitting}>
              {m.label}
            </SegmentedItem>
          ))}
        </SegmentedControl>
        <p className="mt-2 text-[12px] text-text-sub-600 leading-relaxed">
          {MODES.find((m) => m.key === mode)?.hint}
        </p>
      </div>

      {/* Target rider picker. */}
      <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
        {isSwap ? 'Swap with' : 'Move to'}
      </p>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
        {targets.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-text-sub-600">
            No other riders in this dispatch's snapshot.
          </div>
        ) : (
          targets.map((r) => {
            const checked = selectedId === r.id;
            return (
              <label
                key={r.id}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                  checked ? 'bg-bg-weak-50 border-l-2 border-l-accent' : 'border-l-2 border-l-transparent hover:bg-bg-weak-50'
                }`}
              >
                <input
                  type="radio"
                  name="reassign-batches-rider"
                  checked={checked}
                  onChange={() => setSelectedId(r.id)}
                  disabled={submitting}
                  className="h-4 w-4 border-stroke-soft-200 text-accent focus:ring-accent"
                />
                <span className="min-w-0">
                  <span className="block text-[13px] text-text-strong-950 tracking-tight truncate">
                    {r.name}
                  </span>
                  <span className="block font-mono text-[11px] text-text-sub-600 tabular-nums">
                    {r.code}
                  </span>
                </span>
              </label>
            );
          })
        )}
      </div>
    </FormModal>
  );
};

export default ReassignBatchesModal;
